import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react';
import {useConfirmationDialog} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {
    useNavigate,
    useLocation,
    useMatch,
    matchPath,
    useSearchParams,
} from 'react-router-dom';
import {cloneDeep} from 'lodash';
import {useAppContext} from 'contexts/ApplicationContext';
import {useLazyGetJobQuery} from 'components/customer/Job/store/jobApi';
import {produce} from 'immer';
import {useAppSelector} from 'store/customer';
import {selectChanges} from 'store/customer/appSlice';
import {shallowEqual} from 'react-redux';

/**
 * @typedef {import('/home/ishans/work/cbc/gocabinets-ui/src/components/customer/Product/entity/Room.ts').RoomResponse} RoomResponse
 */

/** @type {React.Context<{ room: RoomResponse } | undefined>} */
export const JobContext = createContext();

export const useJobContext = () => useContext(JobContext);

export const JobProvider = ({children, jobId, roomId}) => {
    const {userProfile} = useAppContext();
    const {showDialog, dialog, hideDialog} = useConfirmationDialog();
    const navigate = useNavigate();
    const location = useLocation();

    const [isJobProcessing, setIsJobProcessing] = useState(true);
    const [room, setRoom] = useState({});
    const [roomIndex, setRoomIndex] = useState(1);
    const [isRoomPlanner, setIsRoomPlanner] = useState(false);

    const changes = useAppSelector(selectChanges, shallowEqual);
    const isPageDirty = useMemo(() => changes.length > 0, [changes]);

    const [searchParams] = useSearchParams();

    const isJobDashboardPage = useMatch('/v2/job/:jobId/dashboard');

    const [job, dispatch] = useReducer((state, action) => {
        switch (action.type) {
            case 'SET_JOB':
                return {...state, ...action.payload};
            case 'ADD_PRODUCT_COUNT':
                return produce(state, (draft) => {
                    draft.totalProductCount += action.payload;
                });
            case 'DEDUCT_PRODUCT_COUNT':
                return produce(state, (draft) => {
                    draft.totalProductCount -= action.payload;
                });
            case 'ADD_ROOM_PRODUCT_COUNT':
                return produce(state, (draft) => {
                    const room = draft.rooms.find(
                        (room) => room.id == action.payload.roomId
                    );
                    if (room) {
                        room.productCount += Number(action.payload.count);
                    }
                });
            case 'DEDUCT_ROOM_PRODUCT_COUNT':
                return produce(state, (draft) => {
                    const room = draft.rooms.find(
                        (room) => room.id == action.payload.roomId
                    );
                    if (room) {
                        room.productCount -= action.payload.count;
                    }
                });
            default:
                return state;
        }
    }, {});

    const setJob = useCallback((job) => {
        dispatch({type: 'SET_JOB', payload: job});
    }, []);

    const [getJobDetails, {data: jobData, error}] = useLazyGetJobQuery();

    const expiredJobPrice = useMemo(() => {
        return job.priceExpire;
    }, [job]);

    const navigateToRoom = useCallback(
        (jobId, roomId) => {
            navigate(`/v2/job/${jobId}/room/${roomId}/dashboard`);
        },
        [navigate]
    );

    useEffect(() => {
        setIsJobProcessing(true);
        if (jobId && !isNaN(jobId)) {
            getJobDetails({
                jobId: parseInt(jobId) - 10000,
                timestamp: Date.now(), // this is used to force a re-fetch. It's not used in the API
            });
        }
    }, [jobId]);

    useEffect(() => {
        if (job && job.rooms && job.rooms.length > 0) {
            if (roomId) {
                const roomDetails = job.rooms.find((room) => room.id == roomId);

                if (typeof roomDetails !== 'undefined') {
                    setRoom(cloneDeep(roomDetails));
                }
            } else {
                if (
                    !!matchPath(
                        '/v2/job/:jobId/room/:roomId/*',
                        location.pathname
                    ) ||
                    !!matchPath('/v2/job/:jobId/dashboard', location.pathname)
                ) {
                    setRoom(cloneDeep(job.rooms[0]));
                }
            }
        }
        if (job?.rooms?.length === 0) {
            setRoom({});
        }
    }, [job, roomId]);

    useEffect(() => {
        if (jobData && !error) {
            setIsJobProcessing(
                jobData.status == 0 ||
                    jobData.status == 11 ||
                    jobData.status == 19
            );
            setJob(cloneDeep(jobData));

            if (
                isJobDashboardPage &&
                roomId == null &&
                jobData.rooms &&
                jobData.rooms.length > 0
            ) {
                const roomDetails = jobData.rooms[0];

                if (roomDetails) {
                    navigateToRoom(jobData.displayId, roomDetails.id);
                }
            }
        } else if (error) {
            setJob({error});
        }
    }, [jobData, error]);

    useEffect(() => {
        if (searchParams.has('deleted-room')) {
            const deletedRoomId = searchParams.get('deleted-room');

            if (job && job.rooms && job.rooms.length > 0) {
                const deletedRoom = job.rooms.find(
                    (room) => room.id == deletedRoomId
                );

                if (
                    typeof deletedRoom === 'undefined' &&
                    deletedRoomId == room.id
                ) {
                    const room = job.rooms[job.rooms.length - 1];

                    navigateToRoom(job.displayId, room.id);
                }
            }
        }
    }, [searchParams, job, room]);

    useEffect(() => {
        if (job && job.rooms && room) {
            const roomIndex = job.rooms.findIndex((r) => r.id === room.id);
            setRoomIndex(roomIndex);
        }
    }, [job, room]);

    useEffect(() => {
        if (typeof roomId == 'undefined') {
            if (room && typeof room.id !== 'undefined') {
                const handleBeforeUnload = (event) => {
                    localStorage.setItem('roomId', room.id);
                };

                window.addEventListener('beforeunload', handleBeforeUnload);

                return () => {
                    window.removeEventListener(
                        'beforeunload',
                        handleBeforeUnload
                    );
                };
            } else {
                // set room here
                if (job && job.rooms) {
                    const persistedRoomId = localStorage.getItem('roomId');
                    const room = job.rooms.find(
                        (room) => room.id == persistedRoomId
                    );

                    if (room) {
                        setRoom(room);
                    }
                    localStorage.removeItem('roomId');
                }
            }
        }
    }, [roomId, room, job]);

    const actionButtonsEnabled = useMemo(() => {
        const enableButton =
            isJobProcessing && userProfile?.inActiveManufacturer == 0;
        const expiredJob = !userProfile?.show_pricing ? false : expiredJobPrice;

        return enableButton && !expiredJob;
    }, [isJobProcessing, userProfile, expiredJobPrice]);

    const updateTotalProductCountManual = useCallback(
        (isAddedItemBenchtop = true, increment = true, quantity = 1) => {
            if (increment) {
                dispatch({
                    type: 'ADD_PRODUCT_COUNT',
                    payload: quantity,
                });

                if (!isAddedItemBenchtop) {
                    dispatch({
                        type: 'ADD_ROOM_PRODUCT_COUNT',
                        payload: {roomId, count: quantity},
                    });
                }
            } else {
                dispatch({
                    type: 'DEDUCT_PRODUCT_COUNT',
                    payload: quantity,
                });

                if (!isAddedItemBenchtop) {
                    dispatch({
                        type: 'DEDUCT_ROOM_PRODUCT_COUNT',
                        payload: {roomId, count: quantity},
                    });
                }
            }
        },
        [roomId]
    );

    return (
        <JobContext.Provider
            value={{
                roomIndex,
                job,
                setJob,
                room,
                setRoom,
                isPageDirty,
                showDialog,
                hideDialog,
                isJobProcessing,
                actionButtonsEnabled,
                expiredJobPrice,
                updateTotalProductCountManual,
                isRoomPlanner,
                setIsRoomPlanner,
                setIsJobProcessing,
            }}>
            {children}
            {dialog}
        </JobContext.Provider>
    );
};
